(() => {
  if (customElements.get('trade-order')) return;

  const searchText = (value) => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const tradeDiscountWarning = 'Added to cart. Trade discount could not be confirmed. Review your cart before checkout.';
  let discountQueue = Promise.resolve();

  const discountApplications = (cart) => {
    const applications = Array.isArray(cart?.cart_level_discount_applications) ? [...cart.cart_level_discount_applications] : [];
    (Array.isArray(cart?.items) ? cart.items : []).forEach((item) => {
      const allocations = [
        ...(Array.isArray(item?.line_level_discount_allocations) ? item.line_level_discount_allocations : []),
        ...(Array.isArray(item?.discount_allocations) ? item.discount_allocations : [])
      ];
      allocations.forEach((allocation) => {
        if (allocation?.discount_application) applications.push(allocation.discount_application);
      });
    });
    return applications.filter((application) => application?.type === 'discount_code');
  };

  const applicationCode = (application) => {
    const value = application.code || application.title;
    return typeof value === 'string' ? value.trim() : '';
  };

  const confirmsDiscount = (cart, code, percentage) => discountApplications(cart).some((application) =>
    applicationCode(application).toLowerCase() === code.toLowerCase() &&
    (percentage === null || application.value_type === 'percentage' && Number(application.value) === percentage));

  const sameOriginUrl = (value) => {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.href);
      return url.origin === window.location.origin && /^https?:$/.test(url.protocol) ? url : null;
    } catch (_) {
      return null;
    }
  };

  const repairVideoUrl = (value) => {
    try {
      const url = new URL(value);
      if (!/^https?:$/.test(url.protocol) || url.username || url.password || url.port) return null;
      let id;
      if (url.hostname === 'youtu.be' || url.hostname === 'www.youtu.be') {
        id = url.pathname.match(/^\/([A-Za-z0-9_-]{11})\/?$/)?.[1];
      } else if (['youtube.com', 'www.youtube.com', 'm.youtube.com'].includes(url.hostname)) {
        id = url.pathname === '/watch' && url.searchParams.getAll('v').length === 1
          ? url.searchParams.get('v')
          : url.pathname.match(/^\/embed\/([A-Za-z0-9_-]{11})\/?$/)?.[1];
      } else if (['youtube-nocookie.com', 'www.youtube-nocookie.com'].includes(url.hostname)) {
        id = url.pathname.match(/^\/embed\/([A-Za-z0-9_-]{11})\/?$/)?.[1];
      }
      return /^[A-Za-z0-9_-]{11}$/.test(id || '')
        ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`
        : null;
    } catch (_) {
      return null;
    }
  };

  const readRules = (input) => {
    const minimum = Number(input.dataset.min || 1);
    const increment = Number(input.dataset.increment || 1);
    const maximum = input.hasAttribute('max') ? Number(input.getAttribute('max')) : null;
    if (!Number.isSafeInteger(minimum) || minimum < 1 || !Number.isSafeInteger(increment) || increment < 1 ||
      maximum !== null && (!Number.isSafeInteger(maximum) || maximum < 0)) {
      return { error: 'Quantity rules could not be loaded. Reload this page.' };
    }
    return { minimum, increment, maximum };
  };

  const readQuantity = (input) => {
    const quantity = Number(input.value);
    if (input.validity.badInput || !Number.isSafeInteger(quantity)) return { error: 'Enter a whole number.' };
    const rules = readRules(input);
    if (rules.error) return rules;
    if (quantity < rules.minimum) return { error: `Minimum quantity is ${rules.minimum}.` };
    if (rules.maximum !== null && quantity > rules.maximum) return { error: `Maximum quantity is ${rules.maximum}.` };
    if (quantity % rules.increment !== 0) return { error: `Order in multiples of ${rules.increment}.` };
    return { quantity };
  };

  const errorText = (value) => {
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.map(errorText).filter(Boolean).join(' ');
    if (value && typeof value === 'object') return Object.values(value).map(errorText).filter(Boolean).join(' ');
    return '';
  };

  class TradeOrder extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.pendingRows ||= new WeakSet();
      const options = { signal: this.events.signal };
      this.rows = Array.from(this.querySelectorAll('[data-trade-row]'));
      this.products = Array.from(this.querySelectorAll('[data-trade-product]'));
      this.groups = Array.from(this.querySelectorAll('[data-catalogue-group]'));
      this.search = this.querySelector('[data-catalogue-search]');
      this.search?.addEventListener('input', () => this.filterCatalogue(), options);
      this.rows.forEach((row) => {
        const input = row.querySelector('[data-trade-quantity]');
        if (!input) return;
        const form = row.querySelector('[data-trade-form]') || row;
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.addRow(row);
        }, options);
        input.addEventListener('input', () => {
          input.setCustomValidity('');
          input.removeAttribute('aria-invalid');
          this.setStatus(row, '');
          this.updateControls(row);
        }, options);
        input.addEventListener('change', () => this.validateRow(row), options);
        row.querySelector('[data-trade-minus]')?.addEventListener('click', () => this.stepQuantity(row, -1), options);
        row.querySelector('[data-trade-plus]')?.addEventListener('click', () => this.stepQuantity(row, 1), options);
        this.updateControls(row);
      });
      this.querySelectorAll('[data-catalogue-options]').forEach((group) => {
        const select = group.querySelector('[data-catalogue-variant]');
        const rows = Array.from(group.querySelectorAll('[data-trade-row]'));
        if (!select || rows.length < 2) return;
        const showVariant = (preferAvailable = false) => {
          let selected = rows.find((row) => row.dataset.variantId === select.value);
          if (!selected || preferAvailable && selected.dataset.stock === 'out') {
            selected = rows.find((row) => row.dataset.stock !== 'out') || selected || rows[0];
          }
          select.value = selected.dataset.variantId;
          rows.forEach((row) => { row.hidden = row !== selected; });
        };
        showVariant(true);
        select.hidden = false;
        const control = group.querySelector('[data-catalogue-variant-control]');
        if (control) control.hidden = false;
        select.addEventListener('change', () => showVariant(), options);
      });
      this.filterCatalogue();
      this.setupVideo(options);
    }

    disconnectedCallback() {
      this.closeVideo(false);
      this.events?.abort();
      this.events = null;
    }

    setupVideo(options) {
      this.videoModal = this.querySelector('[data-repair-video-modal]');
      this.videoFrame = this.videoModal?.querySelector('[data-repair-video-frame]');
      if (!this.videoFrame || typeof this.videoModal.showModal !== 'function') return;
      this.querySelectorAll('[data-repair-video]').forEach((trigger) => {
        if (!repairVideoUrl(trigger.dataset.videoUrl)) return;
        trigger.setAttribute('aria-haspopup', 'dialog');
        const behaviour = trigger.querySelector('[data-repair-video-behaviour]');
        if (behaviour) behaviour.textContent = ' (opens in a dialog)';
      });
      this.videoModal.addEventListener('close', () => this.clearVideo(), options);
      this.videoModal.addEventListener('keydown', (event) => {
        // Keep the theme's unrelated overlay shortcuts out of this native dialog.
        if (event.key === 'Escape') event.stopPropagation();
      }, options);
      this.videoModal.addEventListener('click', (event) => {
        if (event.target !== this.videoModal) return;
        const bounds = this.videoModal.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right ||
          event.clientY < bounds.top || event.clientY > bounds.bottom) this.closeVideo();
      }, options);
      this.videoModal.querySelectorAll('[data-repair-video-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeVideo(), options);
      });
      this.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const trigger = event.target?.closest?.('[data-repair-video]');
        if (!trigger || trigger.closest('trade-order') !== this) return;
        if (this.openVideo(trigger)) event.preventDefault();
      }, options);
    }

    openVideo(trigger) {
      const url = repairVideoUrl(trigger.dataset.videoUrl);
      if (!url || !this.videoFrame || typeof this.videoModal?.showModal !== 'function') return false;
      const title = trigger.dataset.videoTitle?.trim() || 'Repair video';
      const heading = this.videoModal.querySelector('[data-repair-video-title]');
      if (heading) heading.textContent = title;
      this.videoFrame.title = title;
      try {
        if (!this.videoModal.open) this.videoModal.showModal();
      } catch (_) {
        return false;
      }
      this.videoTrigger = trigger;
      this.videoFrame.src = url;
      this.videoModal.querySelector('[data-repair-video-close]')?.focus({ preventScroll: true });
      return true;
    }

    clearVideo(restoreFocus = true) {
      this.videoFrame?.removeAttribute('src');
      const trigger = this.videoTrigger;
      this.videoTrigger = null;
      if (restoreFocus && trigger?.isConnected) trigger.focus({ preventScroll: true });
    }

    closeVideo(restoreFocus = true) {
      if (!restoreFocus) this.videoTrigger = null;
      if (this.videoModal?.open) this.videoModal.close();
      this.clearVideo(restoreFocus);
    }

    filterCatalogue() {
      const terms = searchText(this.search?.value).split(/\s+/).filter(Boolean);
      this.products.forEach((product) => {
        const text = searchText(product.dataset.search);
        product.hidden = !terms.every((term) => text.includes(term));
      });
      this.groups.forEach((group) => {
        const visible = Array.from(group.querySelectorAll('[data-trade-product]')).filter((product) => !product.hidden).length;
        group.hidden = visible === 0;
        group.querySelectorAll('[data-catalogue-group-count]').forEach((element) => { element.textContent = String(visible); });
      });
      const visible = this.products.filter((product) => !product.hidden).length;
      this.querySelectorAll('[data-catalogue-count]').forEach((element) => { element.textContent = String(visible); });
      this.querySelectorAll('[data-catalogue-empty]').forEach((element) => { element.hidden = visible !== 0; });
    }

    validateRow(row) {
      const input = row.querySelector('[data-trade-quantity]');
      const result = readQuantity(input);
      input.setCustomValidity(result.error || '');
      input.setAttribute('aria-invalid', String(Boolean(result.error)));
      this.setStatus(row, result.error || '', Boolean(result.error));
      this.updateControls(row);
      return result;
    }

    stepQuantity(row, direction) {
      const input = row.querySelector('[data-trade-quantity]');
      if (!input || input.disabled || this.pendingRows.has(row) || row.dataset.stock === 'out') return;
      const rules = readRules(input);
      if (rules.error) return this.setStatus(row, rules.error, true);
      const { minimum, increment, maximum } = rules;
      const value = Number(input.value);
      const current = Number.isFinite(value) && value > 0 ? value : 0;
      const first = Math.ceil(minimum / increment) * increment;
      let next = direction > 0
        ? (Math.floor(current / increment) + 1) * increment
        : (Math.ceil(current / increment) - 1) * increment;
      if (maximum !== null) next = Math.min(next, Math.floor(maximum / increment) * increment);
      next = Math.max(first, next);
      if (!Number.isSafeInteger(next)) return;
      input.value = String(next);
      this.validateRow(row);
    }

    updateControls(row) {
      const input = row.querySelector('[data-trade-quantity]');
      const rules = readRules(input);
      const unavailable = this.pendingRows.has(row) || input.disabled || row.dataset.stock === 'out';
      const minus = row.querySelector('[data-trade-minus]');
      const plus = row.querySelector('[data-trade-plus]');
      const add = row.querySelector('[data-trade-add]');
      const first = Math.ceil(rules.minimum / rules.increment) * rules.increment;
      if (minus) minus.disabled = unavailable || Boolean(rules.error) || Number(input.value) <= first;
      if (plus) {
        const highest = rules.maximum === null ? Infinity : Math.floor(rules.maximum / rules.increment) * rules.increment;
        plus.disabled = unavailable || Boolean(rules.error) || Number(input.value) >= highest;
      }
      if (add) add.disabled = unavailable;
    }

    setStatus(row, message, isError = false) {
      const status = row.querySelector('[data-trade-row-status]');
      const text = row.querySelector('[data-trade-row-message]');
      if (text) text.textContent = message;
      if (status) {
        status.hidden = !message;
        status.classList.toggle('sr-only', !isError);
        status.classList.toggle('is-error', isError);
      }
    }

    async refreshCartCount() {
      const url = sameOriginUrl(this.dataset.cartUrl);
      if (!url) return;
      url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
      const requestId = this.cartRequestId = (this.cartRequestId || 0) + 1;
      try {
        const response = await fetch(url.href, { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) return;
        const cart = await response.json();
        if (requestId !== this.cartRequestId || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0) return;
        const label = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
        document.querySelectorAll('[data-cart-count]').forEach((element) => {
          element.textContent = String(cart.item_count);
          element.classList.toggle('is-empty', cart.item_count === 0);
        });
        document.querySelectorAll('[data-cart-drawer-count]').forEach((element) => { element.textContent = label; });
        document.querySelectorAll('[data-open-cart]').forEach((element) => { element.setAttribute('aria-label', `Cart, ${label}`); });
      } catch (_) {
        // The add succeeded. The cart refreshes again when the customer opens it.
      }
    }

    applyTradeDiscount() {
      const code = (this.dataset.discountCode || '').trim();
      if (!code) return Promise.resolve(true);
      const configuredPercentage = (this.dataset.discountPercent || '').trim();
      const percentage = configuredPercentage === '' ? null : Number(configuredPercentage);
      const apply = async () => {
        try {
          const updateUrl = sameOriginUrl(this.dataset.updateUrl);
          const cartUrl = sameOriginUrl(this.dataset.cartUrl);
          if (!updateUrl || !cartUrl || code.includes(',') ||
            percentage !== null && (!Number.isFinite(percentage) || percentage < 0 || percentage > 100)) return false;
          cartUrl.pathname = `${cartUrl.pathname.replace(/\/$/, '')}.js`;
          const currentResponse = await fetch(cartUrl.href, {
            credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store'
          });
          if (!currentResponse.ok) return false;
          const currentCart = await currentResponse.json();
          if (!Array.isArray(currentCart?.items)) return false;
          if (confirmsDiscount(currentCart, code, percentage)) return true;

          const existingCodes = discountApplications(currentCart).map(applicationCode);
          if (existingCodes.some((existing) => !existing || existing.includes(','))) return false;
          const codes = new Map(existingCodes.map((existing) => [existing.toLowerCase(), existing]));
          codes.set(code.toLowerCase(), code);
          const response = await fetch(updateUrl.href, {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: Array.from(codes.values()).join(',') })
          });
          if (!response.ok) return false;
          const cart = await response.json();
          return confirmsDiscount(cart, code, percentage) && existingCodes.every((existing) => confirmsDiscount(cart, existing, null));
        } catch (_) {
          return false;
        }
      };
      // Each read/merge/update completes before another discount operation starts.
      const operation = discountQueue.then(apply, apply);
      discountQueue = operation.then(() => undefined, () => undefined);
      return operation;
    }

    async addRow(row) {
      if (this.pendingRows.has(row) || row.dataset.stock === 'out') return;
      const input = row.querySelector('[data-trade-quantity]');
      if (!input || input.disabled) return;
      const result = this.validateRow(row);
      if (result.error) {
        input.focus();
        input.reportValidity();
        return;
      }
      const id = Number(row.dataset.variantId);
      const url = sameOriginUrl(this.dataset.addUrl);
      if (!url || !Number.isSafeInteger(id) || id <= 0) {
        this.setStatus(row, 'Product details could not be loaded. Reload this page.', true);
        return;
      }
      this.pendingRows.add(row);
      row.setAttribute('aria-busy', 'true');
      const button = row.querySelector('[data-trade-add]');
      const cartLink = row.querySelector('[data-trade-view-cart]');
      button?.classList.add('is-loading');
      button?.setAttribute('aria-busy', 'true');
      if (cartLink) cartLink.hidden = true;
      const controls = Array.from(row.querySelectorAll('input, button'));
      const disabledStates = controls.map((control) => control.disabled);
      controls.forEach((control) => { control.disabled = true; });
      this.setStatus(row, 'Adding to cart…');
      let addConfirmed = false;
      try {
        const response = await fetch(url.href, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: result.quantity }] })
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          const message = errorText(payload?.description) || errorText(payload?.errors) || errorText(payload?.message);
          this.setStatus(row, `${message || 'Could not add to cart.'} Check your cart before trying again.`, true);
          return;
        }
        const added = Array.isArray(payload?.items) && payload.items.some((item) =>
          Number(item.id) === id && Number.isSafeInteger(item.quantity) && item.quantity > 0);
        if (!added) {
          this.setStatus(row, 'Could not confirm this was added. Check your cart before trying again.', true);
          await this.refreshCartCount();
          return;
        }
        addConfirmed = true;
        const discountConfirmed = await this.applyTradeDiscount();
        this.setStatus(row, discountConfirmed ? 'Added to cart' : tradeDiscountWarning, !discountConfirmed);
        void this.refreshCartCount();
      } catch (_) {
        this.setStatus(row, addConfirmed ? tradeDiscountWarning : 'Could not confirm this was added. Check your cart before trying again.', true);
      } finally {
        this.pendingRows.delete(row);
        row.removeAttribute('aria-busy');
        button?.classList.remove('is-loading');
        button?.removeAttribute('aria-busy');
        // A completed request can be reviewed in the cart, including an uncertain response.
        if (cartLink) cartLink.hidden = false;
        controls.forEach((control, index) => { control.disabled = disabledStates[index]; });
        this.updateControls(row);
      }
    }
  }

  customElements.define('trade-order', TradeOrder);
})();
